import { memo } from "react";
import { ChatMediaAudio } from "./ChatMediaAudio";
import { ChatMediaDocument } from "./ChatMediaDocument";
import { ChatMediaImage } from "./ChatMediaImage";
import { ChatMediaPdf } from "./ChatMediaPdf";
import { useChatMediaUrl } from "./useChatMediaUrl";

export interface ChatMediaItem {
  readonly id: string;
  readonly mimeType: string;
  readonly name: string;
  readonly size: number;
}

/**
 * Routes a chat-media attachment to its WhatsApp-style viewer: images, PDFs,
 * audio and video render as clean inline media (no name/size/type chrome);
 * everything else falls back to a download card that keeps those details.
 */
function ChatMediaAttachmentComponent({
  media,
}: {
  readonly media: ChatMediaItem;
}) {
  const url = useChatMediaUrl(media.id);
  const { mimeType, name, size } = media;
  // Strip MIME parameters (e.g. "application/pdf; charset=binary") so a
  // parameterized type still routes to the right viewer.
  const baseMime = (mimeType.split(";")[0] ?? "").trim().toLowerCase();

  if (baseMime.startsWith("image/")) {
    return <ChatMediaImage url={url} name={name} />;
  }
  if (baseMime.startsWith("audio/")) {
    return <ChatMediaAudio url={url} />;
  }
  if (baseMime === "application/pdf") {
    return <ChatMediaPdf url={url} name={name} />;
  }
  if (baseMime.startsWith("video/")) {
    return url ? (
      <video
        controls
        src={url}
        preload="metadata"
        className="max-h-[280px] w-full rounded-2xl bg-black"
        data-testid="chat-media-video"
      >
        <track kind="captions" />
      </video>
    ) : (
      <div className="h-40 w-full animate-pulse rounded-2xl bg-muted" />
    );
  }
  return (
    <ChatMediaDocument url={url} mimeType={mimeType} name={name} size={size} />
  );
}

// Memoized so an unrelated re-render of the parent bubble doesn't re-run the
// media-URL resolution / viewer routing when the `media` item is unchanged.
export const ChatMediaAttachment = memo(ChatMediaAttachmentComponent);
